/* eslint-disable jsx-a11y/control-has-associated-label */
// The following enables the 'css' prop. See: https://emotion.sh/docs/css-prop
/** @jsxImportSource @emotion/react */

import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import { ENV, Environment, GIT_SHA_SHORT } from 'packages/env';
import {
  Create as AnnotationsIcon,
  Explore as TaxonomyIcon,
  Theaters as VideosIcon,
  Train as TrainingJobsIcon, // Alternate option: DirectionsBike or ImportantDevices
} from '@mui/icons-material';
import {
  SettingOutlined as SettingsIcon,
  SlidersOutlined as MetricsIcon,
} from '@ant-design/icons';
import MineCart from 'packages/lib/assets/icons/minecart-loaded.svg?react';
import { Button, Menu, Tooltip, Typography } from 'antd';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { message } from 'antd';
import MLSiteIcon from 'packages/lib/assets/icons/logo-aktifoB.svg?react';
import { useHasRosterManagementScope } from 'packages/roster-management/use-has-roster-management-scope';
import { ItemType } from 'antd/es/menu/interface';

const HIDE_ROSTER_MANAGEMENT = true;

const ML_SITE_ICON_SIZE = 28;
export const mlSiteIconCSS = {
  width: ML_SITE_ICON_SIZE + 'px',
  height: ML_SITE_ICON_SIZE + 'px',
  ':hover': {
    filter: 'brightness(140%)',
  },
};

const useStyles = makeStyles()(() => ({
  versionWrapper: {
    fill: '#fff',
    color: '#fff',
    alignItems: 'center',
    textAlign: 'center',
    display: 'grid',
    justifyContent: 'center',
    gap: '5px',
    marginBottom: '1rem',
    marginTop: '1rem',
  },
}));

const items: ItemType[] = [
  {
    key: 'annotations',
    label: 'Annotations',
    icon: <AnnotationsIcon />,
    children: [
      { key: '/pipelines', label: 'Pipelines' },
      { key: '/sample-groups', label: 'Sample Groups' },
      { key: '/task-groups', label: 'Task Groups' },
      { key: '/tld-home', label: 'Traffic Light Detection' },
      {
        key: '/dtb',
        label: 'DTB Dataset Release',
      },
      { key: '/task-lookup', label: 'Task Lookup' },
    ],
  },
  {
    key: 'taxonomy',
    label: 'Taxonomy',
    icon: <TaxonomyIcon />,
    children: [
      {
        key: '/taxonomy',
        label: 'Taxonomy',
      },
      {
        key: '/dbs/nutonomy-imdb',
        label: 'IMDB',
      },
      {
        key: '/dbs/nutonomy-mmdb',
        label: 'MMDB',
      },
    ],
  },
  {
    key: 'data-mining-tools',
    label: 'Data Mining',
    icon: <MineCart />,
    children: [
      {
        key: '/omnitag',
        label: 'Omnitag',
      },
      {
        key: '/data-discovery',
        label: 'Data Discovery',
      },
      {
        key: '/log-miner',
        label: 'LogMiner',
      },
    ],
  },
  {
    key: 'jobmetrics',
    label: 'Job Metrics',
    icon: <MetricsIcon />,
    children: [{ key: '/dude-wheres-my-log', label: `Dude, Where's My Log?` }],
  },
  {
    key: '/scene-hunting',
    label: 'Scene Hunting',
    icon: <VideosIcon />,
  },
  {
    key: '/training-jobs',
    label: 'Train Station',
    icon: <TrainingJobsIcon />,
  },
  {
    key: 'management',
    label: 'Management',
    icon: <SettingsIcon />,
    children: [
      {
        key: '/attributes',
        label: 'Attributes',
      },
      {
        key: '/instructions',
        label: 'Instructions',
      },
      {
        key: '/categories',
        label: 'Categories',
      },
    ],
  },
];
// Routes.tsx path -> menuKeys that should be highlighted
const routeKeyMap = new Map<string, string[]>([
  ['/tld-video/:token?/:param?', ['/tld-home']],

  ['/task-group', ['annotations', '/task-groups']],
  ['/task-group/:type?/:taskGroupToken', ['annotations', '/task-groups']],
  ['/task-groups/:type?/:taskGroupToken', ['annotations', '/task-groups']],
  ['/sample-group', ['annotations', '/sample-groups']],
  [
    '/sample-groups/:workflowToken/:sampleGroupToken',
    ['annotations', '/sample-groups'],
  ],
  ['/sample-group/:sampleGroupToken', ['annotations', '/sample-group']],
  ['/pipelines', ['annotations', '/pipelines']],
  ['/workflow/:workflowToken', ['annotations', '/pipelines']],
  ['/reports', ['annotations', '/reports']],
  ['/reports/:path+', ['annotations', '/reports']],
  ['/tld-home', ['annotations', '/tld-home']],
  ['/tld-video/:token?/:param?', ['annotations', '/tld-home']],

  ['/taxonomy/:categoryPath?/:activePage?', ['taxonomy', '/taxonomy']],
  ['/dbs/:bucket/:key*', ['taxonomy']],
  ['/dbu/:bucket/:key*', ['taxonomy']],

  ['/data-discovery/:query?', ['log-miner']],

  ['/categories', ['management']],
  ['/instructions', ['management']],
  ['/attributes', ['management']],

  ['/categories/:token', ['management', '/categories']],
  ['/instructions/:token', ['management', '/instructions']],
  ['/attributes/:token', ['management', '/attributes']],
  ['/roster-management', ['management', '/roster-management']],
]);

export const MainNav: FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const m = useRouteMatch();
  const canUseRosterManagement = useHasRosterManagementScope();

  if (canUseRosterManagement && !HIDE_ROSTER_MANAGEMENT) {
    (
      items.find((item) => item?.key === 'management') as ItemType & {
        children: ItemType[];
      }
    )?.children?.push({
      key: '/roster-management',
      label: 'Roster Management',
    });
  }

  const selectedKeys: string[] = routeKeyMap.get(m.path) ?? [];
  // handles the menu items that belong to the same route but specific ids eg /dbs/imdb /dbs/mmdb
  selectedKeys.push(m.url);
  const onClickCopyVersion = async (): Promise<void> => {
    try {
      await window.navigator.clipboard.writeText(GIT_SHA_SHORT);
      void message.success('Version has been copied to the clipboard');
    } catch (e) {
      void message.error('We failed to copy the version to your clipboard', 5);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100%',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '10px',
        }}
      >
        <Typography.Title level={2} style={{ marginBottom: 0 }}>
          <a href="/" style={{ color: '#fff' }}>
            <MLSiteIcon
              css={{
                ...mlSiteIconCSS,
                marginTop: '8px',
              }}
            />
          </a>
        </Typography.Title>
      </header>

      {/* FIXME: This is causing a 'Can't perform React state update on unmounted component' error here when user navigates to a page that
      does not use main-nav, such as localhost:3000/tools
      Try upgrading to a newer version of Ant to see if it's fixed that way. */}
      <Menu
        style={{ flexGrow: 1 }}
        selectedKeys={selectedKeys}
        mode="inline"
        theme="dark"
        items={items}
        onClick={(menuItem): void => {
          if (menuItem.key.startsWith('/')) history.push(menuItem.key);
        }}
      />

      <div className={classes.versionWrapper}>
        {ENV !== Environment.production && ENV !== Environment.local ? (
          <Typography.Title level={5} style={{ marginTop: 10 }}>
            <a href="/" style={{ color: '#fff' }}>
              {ENV}
            </a>
          </Typography.Title>
        ) : null}
        <Typography.Text style={{ color: '#fff' }}>
          Version: {GIT_SHA_SHORT}
        </Typography.Text>
        <Tooltip title="Copy version to clipboard">
          <Button
            ghost
            size="small"
            onClick={onClickCopyVersion}
            style={{ width: '64px', justifySelf: 'center' }}
          >
            Copy
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default MainNav;
